import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { seriesPaletteStyle, StackedArea } from './StackedArea.js';
import { useTheme } from './ThemeProvider.js';

/**
 * The analytics screen.
 *
 * Everything on it is read from our own tables, so a ninety-day range costs
 * the same as a seven-day one. See apps/api/src/services/insightsQuery.ts.
 *
 * Two charts rather than one with two scales. Posts and comments differ by an
 * order of magnitude — one or two posts against fifty comments — and putting
 * them on a shared axis would flatten the smaller series into the baseline.
 * Two axes on one chart would be worse: the crossing point would be an
 * artefact of the scales, and people read it as a fact.
 */

const PRESETS = [7, 30, 90] as const;

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function InsightsPanel() {
  const { t, language } = useI18n();
  const locale = language === 'ar' ? 'ar' : 'en';
  const queryClient = useQueryClient();
  const { theme } = useTheme();

  const [preset, setPreset] = useState<number | 'custom'>(7);
  const [from, setFrom] = useState(isoDaysAgo(6));
  const [to, setTo] = useState(today());
  const [pageId, setPageId] = useState('');

  const range = preset === 'custom' ? { from, to } : { from: isoDaysAgo(preset - 1), to: today() };

  const data = useQuery({
    queryKey: ['insights', range.from, range.to, pageId],
    queryFn: () => api.insightsReport({ ...range, pageId: pageId || undefined }),
  });

  /*
   * Polled only while a backfill is actually running, and stopped the moment
   * it finishes. A progress bar that keeps asking after it reaches the end is
   * a background request nobody remembers writing.
   */
  const status = useQuery({
    queryKey: ['collection-status'],
    queryFn: api.collectionStatus,
    refetchInterval: (query) => (query.state.data && query.state.data.percent < 100 ? 4000 : false),
  });

  const refresh = useMutation({
    mutationFn: api.refreshInsights,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['insights'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['collection-status'] });
    },
  });

  const report = data.data;

  /*
   * Derived once and shared by all three charts. A colour belongs to a Page,
   * so the mapping must not be recomputed per chart — otherwise the same band
   * would be blue in one and orange in the next.
   */
  const bands = (report?.byPageSeries.pages ?? []).map((page) => ({
    ...page,
    name: page.pageId === 'other' ? t('insights.otherPages') : page.name,
  }));
  const days = (report?.byPageSeries.days ?? []).map((day) => day.day);

  return (
    <div className="insights">
      <section className="panel">
        <div className="panel-head">
          <h2>{t('insights.title')}</h2>
          <button
            type="button"
            className="ghost small"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <Icon.refresh className={refresh.isPending ? 'spin' : undefined} />
            {refresh.isPending ? t('insights.refreshing') : t('insights.refresh')}
          </button>
        </div>

        {/* Filters in one row above the charts, so changing the question never
            moves the answers. */}
        <div className="filters">
          <div className="preset-row">
            {PRESETS.map((days) => (
              <button
                key={days}
                type="button"
                className={preset === days ? 'subtab active' : 'subtab'}
                onClick={() => setPreset(days)}
              >
                {t('insights.lastDays', { count: days })}
              </button>
            ))}
            <button
              type="button"
              className={preset === 'custom' ? 'subtab active' : 'subtab'}
              onClick={() => setPreset('custom')}
            >
              {t('insights.custom')}
            </button>
          </div>

          {preset === 'custom' && (
            <div className="date-row">
              <label htmlFor="from">{t('insights.from')}</label>
              <input
                id="from"
                type="date"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
              />
              <label htmlFor="to">{t('insights.to')}</label>
              <input
                id="to"
                type="date"
                value={to}
                min={from}
                max={today()}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          )}

          <DestinationFilter
            feature={DestinationFeature.INSIGHTS}
            value={pageId ? pageDestinationId(pageId) : ''}
            allLabel={t('insights.allPages')}
            onChange={(id) => setPageId(pageIdOf(id))}
          />

          {/*
            Plain links rather than fetch-and-blob.

            The browser already knows how to download a URL, and the server
            already sends the filename in a Content-Disposition header — going
            through JavaScript would mean re-deriving that name on the client
            and holding the whole file in memory to hand it back.

            Three files because the questions differ: how did we do over time,
            which Pages, which posts.
          */}
          <div className="preset-row">
            {(['daily', 'pages', 'posts'] as const).map((kind) => (
              <a
                key={kind}
                className="subtab"
                href={`/api/insights/report.csv?${new URLSearchParams({
                  from: range.from,
                  to: range.to,
                  kind,
                  ...(pageId ? { pageId } : {}),
                }).toString()}`}
                download
              >
                {t(`insights.export.${kind}` as never)}
              </a>
            ))}
          </div>
        </div>

        {refresh.isError && (
          <div className="notice error">
            {refresh.error instanceof ApiError
              ? refresh.error.message
              : t('insights.refreshFailed')}
          </div>
        )}

        {/* A figure still being assembled must not be read as a final one — and
            "please wait" is not an answer to "how long". The bar carries the
            actual count, so the wait has an end in sight. */}
        {/* Shown while a first read is running *or* while a manual refresh is in
            flight. Backfill progress is a real percentage; a refresh has no
            countable stages, so it says what it is doing without pretending to
            know how far along it is. */}
        {refresh.isPending && status.data?.percent === 100 && (
          <div className="collect-progress">
            <div className="collect-head">
              <Icon.refresh className="spin" />
              <span>{t('insights.refreshing')}</span>
            </div>
            <div className="progress-track indeterminate">
              <div className="progress-fill" />
            </div>
            <span className="muted small">
              {t('insights.refreshHint', { count: status.data.total })}
            </span>
          </div>
        )}

        {status.data && status.data.percent < 100 && (
          <div className="collect-progress">
            <div className="collect-head">
              <Icon.refresh className="spin" />
              <span>{t('insights.backfilling')}</span>
              <strong>{status.data.percent}%</strong>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${status.data.percent}%` }} />
            </div>
            <span className="muted small">
              {t('insights.progressCount', {
                done: status.data.done,
                total: status.data.total,
              })}
            </span>
          </div>
        )}

        {data.isLoading && <div className="loading">{t('common.loading')}</div>}

        {report && (
          <>
            <div className="tiles">
              <Metric
                label={t('insights.posts')}
                value={report.totals.posts}
                change={report.change.posts}
                tone="publish"
              />
              <Metric
                label={t('insights.comments')}
                value={report.totals.comments}
                change={report.change.comments}
                tone="engage"
              />
              <Metric
                label={t('insights.reactions')}
                value={report.totals.reactions}
                change={report.change.reactions}
                tone="pages"
              />
              <Metric
                label={t('insights.shares')}
                value={report.totals.shares}
                change={report.change.shares}
                tone="jobs"
              />
              {/* Shown only once Meta actually gives us the figure. An absent
                  measure is left out rather than displayed as a zero. */}
              {report.totals.reach !== null && (
                <Metric
                  label={t('insights.reach')}
                  value={report.totals.reach}
                  change={report.change.reach}
                  tone="home"
                />
              )}
            </div>

            <div className="area-charts" style={seriesPaletteStyle(theme === 'dark')}>
              <StackedArea
                title={t('insights.postsPerDay')}
                bands={bands}
                days={days}
                values={report.byPageSeries.days.map((d) => d.posts)}
                locale={locale}
                emptyLabel={t('insights.noData')}
              />
              <StackedArea
                title={t('insights.commentsPerDay')}
                bands={bands}
                days={days}
                values={report.byPageSeries.days.map((d) => d.comments)}
                locale={locale}
                emptyLabel={t('insights.noData')}
              />
              <StackedArea
                title={t('insights.reactionsPerDay')}
                bands={bands}
                days={days}
                values={report.byPageSeries.days.map((d) => d.reactions)}
                locale={locale}
                emptyLabel={t('insights.noData')}
              />
            </div>

            {report.collectedAt && (
              <p className="muted small">
                {t('insights.collectedAt', {
                  when: new Date(report.collectedAt).toLocaleString(locale),
                })}
              </p>
            )}
          </>
        )}
      </section>

      {report && report.pages.length > 0 && (
        <section className="panel">
          <h2>{t('insights.byPage')}</h2>
          <div className="grid-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('insights.page')}</th>
                  <th>{t('insights.posts')}</th>
                  <th>{t('insights.comments')}</th>
                  <th>{t('insights.reactions')}</th>
                </tr>
              </thead>
              <tbody>
                {report.pages.map((page) => (
                  <tr key={page.pageId}>
                    <td>{page.name}</td>
                    <td>{page.posts}</td>
                    <td>{page.comments}</td>
                    <td>{page.reactions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {report && report.topPosts.length > 0 && (
        <section className="panel">
          <h2>{t('insights.topPosts')}</h2>
          <ul className="top-posts">
            {report.topPosts.map((post) => (
              <li key={post.externalPostId}>
                <div className="top-post-body">
                  <strong>{post.excerpt ?? t('common.noText')}</strong>
                  <span className="muted small">
                    {post.pageName} · {new Date(post.publishedAt).toLocaleDateString(locale)}
                  </span>
                </div>
                <span className="top-post-counts">
                  <span title={t('insights.comments')}>
                    <Icon.comment /> {post.comments}
                  </span>
                  <span title={t('insights.reactions')}>
                    <Icon.check /> {post.reactions}
                  </span>
                </span>
                {post.permalink && (
                  <a href={post.permalink} target="_blank" rel="noreferrer" className="ghost small">
                    {t('insights.open')}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  change,
  tone,
}: {
  label: string;
  value: number;
  change: number | null;
  tone: string;
}) {
  return (
    <div className="tile" data-tone={tone}>
      <span className="tile-body">
        <strong className="tile-value">{value}</strong>
        <span className="tile-label">{label}</span>
        {/* Nothing at all when the previous period was zero. Growth from
            nothing is not a percentage, and printing one would be a lie
            dressed as precision. */}
        {change !== null && (
          <span className={change >= 0 ? 'delta up' : 'delta down'}>
            {change >= 0 ? '+' : ''}
            {change}%
          </span>
        )}
      </span>
    </div>
  );
}
